import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckBoxOutlined as CheckBoxOutlinedIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  DoneAll as DoneAllIcon,
  FileDownload as FileDownloadIcon,
  Search as SearchIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ClienteSearch from '../../../components/Tesoreria/ClienteSearch';
import VendedorSelect from '../../../components/VendedorSelect';
import { SearchableSelect } from '../../../components/SearchableSelect';
import clienteService from '../../../services/clienteService';
import direccionClienteService from '../../../services/direccionClienteService';
import {
  VISITAS_AGENDA_DIA_KEYS,
  VISITAS_AGENDA_DIA_LABELS,
  VisitasAgendaDiaKey,
  VisitasAgendaFila,
  emptyVisitasAgendaDias,
  stripDireccionFromAgendaNombre,
} from '../../../types/visitasAgenda';
import {
  DEFAULT_AGENDA_ID,
  DEFAULT_AGENDA_NOMBRE,
  loadVisitasAgendaFilas,
  saveVisitasAgendaFilas,
} from '../../../services/visitasAgendaStorage';
import {
  exportVisitasAgendaExcel,
  mergeVisitasAgendaFilas,
  parseVisitasAgendaExcel,
} from '../../../services/visitasAgendaImport';
import { loadDireccionesForVisitRow } from './planVisitaRowLoaders';
import { getApiErrorMessage } from '../../../utils/apiError';

const newRowId = (): string =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `fila-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const serializeFilasSnapshot = (filas: VisitasAgendaFila[]): string =>
  JSON.stringify(
    filas.map((f) => ({
      idVisitasAgenda: f.idVisitasAgenda,
      nroCliente: f.nroCliente,
      codigoDireccion: f.codigoDireccion ?? undefined,
      nombreDisplay: f.nombreDisplay,
      dias: { ...emptyVisitasAgendaDias(), ...f.dias },
    })),
  );

const TABLE_COL_SPAN = 9;

const DIA_CHIP_LABELS: Record<VisitasAgendaDiaKey, string> = {
  lun: 'Lun',
  mar: 'Mar',
  mier: 'Mié',
  jue: 'Jue',
  vie: 'Vie',
  sab: 'Sáb',
  dom: 'Dom',
};

const clienteCellSx = {
  width: '26%',
  minWidth: 160,
  maxWidth: 320,
  overflow: 'hidden',
} as const;

const diaCellSx = {
  width: 88,
  minWidth: 72,
  maxWidth: 96,
  px: 0.5,
  whiteSpace: 'nowrap',
} as const;

const normalizeSearch = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const VisitasAgendaTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [draftFilas, setDraftFilas] = useState<VisitasAgendaFila[]>([]);
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [clienteSearchOpen, setClienteSearchOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const [filterText, setFilterText] = useState('');
  const [filterVendedor, setFilterVendedor] = useState<number | null>(null);
  const [filterZona, setFilterZona] = useState<number | null>(null);
  const [filterCiudad, setFilterCiudad] = useState<number | null>(null);
  const [filterDias, setFilterDias] = useState<Set<VisitasAgendaDiaKey>>(() => new Set());

  const deferredFilterText = useDeferredValue(filterText);

  const loadAgendaDraft = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setSelectionMode(false);
    setSelectedIds(new Set());
    try {
      const filas = await loadVisitasAgendaFilas();
      const normalized = filas.map((f) => ({
        ...f,
        nombreDisplay: stripDireccionFromAgendaNombre(f.nombreDisplay, f.codigoDireccion),
        dias: { ...emptyVisitasAgendaDias(), ...f.dias },
      }));
      setDraftFilas(normalized);
      setSavedSnapshot(serializeFilasSnapshot(normalized));
    } catch (error: unknown) {
      setLoadError(getApiErrorMessage(error, 'No se pudo cargar la agenda.'));
      setDraftFilas([]);
      setSavedSnapshot(serializeFilasSnapshot([]));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAgendaDraft();
  }, [loadAgendaDraft]);

  const isDirty = useMemo(
    () => serializeFilasSnapshot(draftFilas) !== savedSnapshot,
    [draftFilas, savedSnapshot],
  );

  const zonaOptions = useMemo(() => {
    const map = new Map<number, string>();
    draftFilas.forEach((f) => {
      if (f.codigoRuta == null) return;
      const label = f.nombreRuta?.trim() || `Zona ${f.codigoRuta}`;
      if (!map.has(f.codigoRuta)) map.set(f.codigoRuta, label);
    });
    return Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1], 'es'))
      .map(([value, label]) => ({ value, label }));
  }, [draftFilas]);

  const ciudadOptions = useMemo(() => {
    const map = new Map<number, string>();
    draftFilas.forEach((f) => {
      if (f.codigoCiudad == null) return;
      const label = f.nombreCiudad?.trim() || `Ciudad ${f.codigoCiudad}`;
      if (!map.has(f.codigoCiudad)) map.set(f.codigoCiudad, label);
    });
    return Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1], 'es'))
      .map(([value, label]) => ({ value, label }));
  }, [draftFilas]);

  const filteredFilas = useMemo(() => {
    const q = normalizeSearch(deferredFilterText);
    return draftFilas.filter((row) => {
      if (q) {
        const haystack = normalizeSearch(
          [row.nombreDisplay, row.codigoDireccion, String(row.nroCliente)].filter(Boolean).join(' '),
        );
        if (!haystack.includes(q)) return false;
      }
      if (filterVendedor != null && Number(row.nroVendedor) !== Number(filterVendedor)) {
        return false;
      }
      if (filterZona != null && Number(row.codigoRuta) !== Number(filterZona)) {
        return false;
      }
      if (filterCiudad != null && Number(row.codigoCiudad) !== Number(filterCiudad)) {
        return false;
      }
      if (filterDias.size > 0) {
        let matchDay = false;
        filterDias.forEach((dia) => {
          if (row.dias[dia]) matchDay = true;
        });
        if (!matchDay) return false;
      }
      return true;
    });
  }, [draftFilas, deferredFilterText, filterVendedor, filterZona, filterCiudad, filterDias]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filterText.trim()) n += 1;
    if (filterVendedor != null) n += 1;
    if (filterZona != null) n += 1;
    if (filterCiudad != null) n += 1;
    if (filterDias.size > 0) n += 1;
    return n;
  }, [filterText, filterVendedor, filterZona, filterCiudad, filterDias]);

  const clearFilters = () => {
    setFilterText('');
    setFilterVendedor(null);
    setFilterZona(null);
    setFilterCiudad(null);
    setFilterDias(new Set());
  };

  const toggleFilterDia = (dia: VisitasAgendaDiaKey) => {
    setFilterDias((prev) => {
      const next = new Set(prev);
      if (next.has(dia)) next.delete(dia);
      else next.add(dia);
      return next;
    });
  };

  const handleToggleDia = (idVisitasAgenda: string, dia: keyof VisitasAgendaFila['dias']) => {
    setDraftFilas((prev) =>
      prev.map((row) =>
        row.idVisitasAgenda === idVisitasAgenda
          ? { ...row, dias: { ...row.dias, [dia]: !row.dias[dia] } }
          : row,
      ),
    );
  };

  const handleRemoveRow = (idVisitasAgenda: string) => {
    setDraftFilas((prev) => prev.filter((row) => row.idVisitasAgenda !== idVisitasAgenda));
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const handleToggleSelectionMode = () => {
    if (selectionMode) {
      exitSelectionMode();
      return;
    }
    setSelectionMode(true);
  };

  const handleSelectAll = () => {
    setSelectedIds(new Set(filteredFilas.map((f) => f.idVisitasAgenda)));
  };

  const handleToggleRowSelected = (idVisitasAgenda: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(idVisitasAgenda)) {
        next.delete(idVisitasAgenda);
      } else {
        next.add(idVisitasAgenda);
      }
      return next;
    });
  };

  const handleDeleteSelected = () => {
    if (selectedIds.size === 0) {
      enqueueSnackbar('Seleccione al menos un cliente.', { variant: 'warning' });
      return;
    }
    if (
      !window.confirm(
        `¿Eliminar ${selectedIds.size} cliente${selectedIds.size === 1 ? '' : 's'} de la agenda?`,
      )
    ) {
      return;
    }
    setDraftFilas((prev) => prev.filter((f) => !selectedIds.has(f.idVisitasAgenda)));
    exitSelectionMode();
  };

  const handleClienteSelected = async (cliente: {
    nroCliente: number;
    nombre?: string;
    razonSocial?: string;
  }) => {
    const nombreDisplay =
      [cliente.nombre, cliente.razonSocial].filter(Boolean).join(' — ') ||
      `Cliente ${cliente.nroCliente}`;
    const [dirPatch, clienteFull, direcciones] = await Promise.all([
      loadDireccionesForVisitRow(cliente.nroCliente),
      clienteService.getCliente(cliente.nroCliente).catch(() => null),
      direccionClienteService.getDirecciones(cliente.nroCliente).catch(() => []),
    ]);

    const autoDir =
      (dirPatch.codigoDireccion
        ? direcciones.find((d) => d.codigoDireccion === dirPatch.codigoDireccion)
        : null) ?? (direcciones.length === 1 ? direcciones[0] : null);

    const street = autoDir?.direccion?.trim() || '';
    const nueva: VisitasAgendaFila = {
      idVisitasAgenda: newRowId(),
      nroCliente: cliente.nroCliente,
      codigoDireccion: dirPatch.codigoDireccion || undefined,
      nombreDisplay: street ? `${nombreDisplay} — ${street}` : nombreDisplay,
      dias: emptyVisitasAgendaDias(),
      nroVendedor: autoDir?.vendedor ?? clienteFull?.vendedor ?? null,
      nombreVendedor: clienteFull?.vendedorInfo?.nombreVendedor ?? null,
      codigoRuta: autoDir?.codigoRuta ?? clienteFull?.codigoRuta ?? null,
      nombreRuta: null,
      codigoCiudad: autoDir?.codigoCiudad ?? clienteFull?.ciudad?.codigoCiudad ?? clienteFull?.codigoCiudad ?? null,
      nombreCiudad: clienteFull?.ciudad?.nombreCiudad ?? null,
    };
    setDraftFilas((prev) => [...prev, nueva]);
    setClienteSearchOpen(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await saveVisitasAgendaFilas(DEFAULT_AGENDA_ID, draftFilas);
      if (!saved) {
        enqueueSnackbar('No se pudo guardar la agenda.', { variant: 'error' });
        return;
      }
      setDraftFilas(saved.filas);
      setSavedSnapshot(serializeFilasSnapshot(saved.filas));
      enqueueSnackbar(`Agenda "${DEFAULT_AGENDA_NOMBRE}" guardada (${saved.filas.length} filas).`, {
        variant: 'success',
      });
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar la agenda.'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleExportExcel = () => {
    if (draftFilas.length === 0) {
      enqueueSnackbar('No hay filas para exportar.', { variant: 'warning' });
      return;
    }
    try {
      exportVisitasAgendaExcel(draftFilas);
      enqueueSnackbar(`Exportadas ${draftFilas.length} filas.`, { variant: 'success' });
    } catch (error: unknown) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'No se pudo exportar el Excel.',
        { variant: 'error' },
      );
    }
  };

  const handleImportFile = async (file: File) => {
    setImporting(true);
    try {
      const result = await parseVisitasAgendaExcel(file);
      if (result.filas.length === 0) {
        const detalle =
          result.errores.length > 0
            ? result.errores
                .slice(0, 3)
                .map((e) => `Fila ${e.fila}: ${e.mensaje}`)
                .join(' — ')
            : 'Revise columnas Nro Cuenta y FRECUENCIA/días.';
        enqueueSnackbar(detalle, { variant: 'warning', autoHideDuration: 8000 });
        return;
      }

      const merged = mergeVisitasAgendaFilas(draftFilas, result.filas, newRowId);
      if (merged.agregados === 0 && merged.actualizados === 0) {
        enqueueSnackbar(
          `Sin cambios: ${merged.sinCambios} fila${merged.sinCambios === 1 ? '' : 's'} ya coinciden con la agenda.`,
          { variant: 'info' },
        );
        return;
      }

      if (
        !window.confirm(
          `Se agregarán ${merged.agregados} cliente${merged.agregados === 1 ? '' : 's'} y se actualizarán ${merged.actualizados}. Los demás de la agenda se mantienen. ¿Continuar?`,
        )
      ) {
        return;
      }

      setDraftFilas(merged.filas);
      const omitidas =
        result.errores.length > 0 ? ` ${result.errores.length} fila(s) omitidas.` : '';
      enqueueSnackbar(
        `Agenda actualizada en pantalla: +${merged.agregados} / ~${merged.actualizados}.${omitidas} Pulse Guardar cambios.`,
        { variant: result.errores.length ? 'warning' : 'info' },
      );
    } catch (error: unknown) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Error al leer el archivo Excel.',
        { variant: 'error', autoHideDuration: 6000 },
      );
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" sx={{ flex: 1 }}>
          {DEFAULT_AGENDA_NOMBRE}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          disabled={loading || draftFilas.length === 0 || selectionMode}
          onClick={handleExportExcel}
        >
          Exportar Excel
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          disabled={importing || loading || selectionMode}
          onClick={() => fileInputRef.current?.click()}
        >
          Importar Excel
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleImportFile(file);
          }}
        />
        {selectionMode ? (
          <>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DoneAllIcon />}
              disabled={loading || filteredFilas.length === 0}
              onClick={handleSelectAll}
            >
              Seleccionar todo
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={loading || selectedIds.size === 0}
              onClick={handleDeleteSelected}
            >
              Eliminar{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={exitSelectionMode}
            >
              Cancelar
            </Button>
          </>
        ) : (
          <Button
            size="small"
            variant="outlined"
            startIcon={<CheckBoxOutlinedIcon />}
            disabled={loading || draftFilas.length === 0}
            onClick={handleToggleSelectionMode}
          >
            Seleccionar
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={loading || selectionMode}
          onClick={() => setClienteSearchOpen(true)}
        >
          Agregar cliente
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Configure qué clientes se visitan cada día de la semana. Al crear un plan, use{' '}
        <strong>Autocompletar</strong> con el vendedor (solo clientes asignados a ese vendedor). Los
        datos se guardan en el servidor.
      </Typography>

      <Box
        sx={{
          mb: 1.5,
          p: 1.25,
          borderRadius: 1,
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Buscar"
              placeholder="Nombre o dirección"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: filterText ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Limpiar búsqueda"
                      onClick={() => setFilterText('')}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <VendedorSelect
              size="small"
              value={filterVendedor}
              onChange={(nro) => setFilterVendedor(nro)}
              allowEmpty
              emptyOptionLabel="Todos"
              label="Vendedor"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <SearchableSelect
              size="small"
              label="Zona"
              options={zonaOptions}
              value={filterZona}
              onChange={(v) => setFilterZona(v)}
              allowEmpty
              emptyOptionLabel="Todas"
              noOptionsText="Sin zonas en la agenda"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <SearchableSelect
              size="small"
              label="Ciudad"
              options={ciudadOptions}
              value={filterCiudad}
              onChange={(v) => setFilterCiudad(v)}
              allowEmpty
              emptyOptionLabel="Todas"
              noOptionsText="Sin ciudades en la agenda"
            />
          </Grid>
          <Grid item xs={12}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                  Días:
                </Typography>
                {VISITAS_AGENDA_DIA_KEYS.map((dia) => {
                  const selected = filterDias.has(dia);
                  return (
                    <Chip
                      key={dia}
                      size="small"
                      label={DIA_CHIP_LABELS[dia]}
                      clickable
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => toggleFilterDia(dia)}
                      title={VISITAS_AGENDA_DIA_LABELS[dia]}
                      sx={{ height: 26 }}
                    />
                  );
                })}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Typography variant="caption" color="text.secondary">
                  {loading
                    ? 'Cargando…'
                    : activeFilterCount > 0
                      ? `Mostrando ${filteredFilas.length} de ${draftFilas.length}`
                      : `${draftFilas.length} cliente${draftFilas.length === 1 ? '' : 's'}`}
                </Typography>
                {activeFilterCount > 0 ? (
                  <Button size="small" onClick={clearFilters} startIcon={<ClearIcon />}>
                    Limpiar
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {loadError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      ) : null}

      {isDirty ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Hay cambios sin guardar.
        </Alert>
      ) : null}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={32} />
        </Stack>
      ) : (
        <TableContainer
          sx={{
            maxHeight: 'calc(100vh - 420px)',
            overflowX: 'auto',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                {selectionMode ? (
                  <TableCell padding="checkbox" sx={{ width: 48 }} />
                ) : null}
                <TableCell sx={clienteCellSx}>Cliente / dirección</TableCell>
                {VISITAS_AGENDA_DIA_KEYS.map((dia) => (
                  <TableCell key={dia} align="center" sx={diaCellSx}>
                    {VISITAS_AGENDA_DIA_LABELS[dia]}
                  </TableCell>
                ))}
                {!selectionMode ? <TableCell align="center" sx={{ width: 56 }} /> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {draftFilas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_COL_SPAN}>
                    No hay filas. Agregue clientes o importe un Excel.
                  </TableCell>
                </TableRow>
              ) : filteredFilas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_COL_SPAN}>
                    Ningún cliente coincide con los filtros.
                  </TableCell>
                </TableRow>
              ) : (
                filteredFilas.map((row) => (
                  <TableRow
                    key={row.idVisitasAgenda}
                    hover
                    selected={selectionMode && selectedIds.has(row.idVisitasAgenda)}
                    onClick={
                      selectionMode
                        ? () => handleToggleRowSelected(row.idVisitasAgenda)
                        : undefined
                    }
                    sx={selectionMode ? { cursor: 'pointer' } : undefined}
                  >
                    {selectionMode ? (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedIds.has(row.idVisitasAgenda)}
                          tabIndex={-1}
                          disableRipple
                          sx={{ pointerEvents: 'none' }}
                          inputProps={{ 'aria-label': `Seleccionar ${row.nombreDisplay}` }}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell sx={clienteCellSx}>
                      <Typography
                        variant="body2"
                        noWrap
                        title={row.nombreDisplay}
                        sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {row.nombreDisplay}
                      </Typography>
                      {row.codigoDireccion ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          title={row.codigoDireccion}
                          sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {row.codigoDireccion}
                        </Typography>
                      ) : null}
                    </TableCell>
                    {VISITAS_AGENDA_DIA_KEYS.map((dia) => (
                      <TableCell key={dia} align="center" padding="checkbox" sx={diaCellSx}>
                        <Checkbox
                          size="small"
                          checked={row.dias[dia]}
                          disabled={selectionMode}
                          tabIndex={selectionMode ? -1 : undefined}
                          disableRipple={selectionMode}
                          sx={selectionMode ? { pointerEvents: 'none' } : undefined}
                          onChange={() => handleToggleDia(row.idVisitasAgenda, dia)}
                        />
                      </TableCell>
                    ))}
                    {!selectionMode ? (
                      <TableCell align="center">
                        <Tooltip title="Quitar fila">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveRow(row.idVisitasAgenda)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
        sx={{
          mt: 2,
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Button
          variant="outlined"
          disabled={!isDirty || saving || loading}
          onClick={() => void loadAgendaDraft()}
        >
          Descartar cambios
        </Button>
        <Button variant="contained" disabled={!isDirty || saving || loading} onClick={() => void handleSave()}>
          {saving ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </Stack>

      <ClienteSearch
        open={clienteSearchOpen}
        onClose={() => setClienteSearchOpen(false)}
        onSelect={handleClienteSelected}
      />
    </Box>
  );
};

export default VisitasAgendaTab;
